import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { BACKEND_URL } from "../config";
import HistoryView from "./HistoryView";
import './ExerciseTracker.css';

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISO = () => toISODate(new Date());

function addDaysISO(dateStr, delta) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return toISODate(d);
}

function normalizeAppLog(log, idx = 0) {
  const duration = log.duration_minutes ?? log.duration ?? log.minutes ?? log.durationMin ?? 0;
  const calories = log.calories_burned ?? log.calories ?? log.kcal ?? log.caloriesBurned ?? 0;
  const startedAt = log.started_at || log.start_time || log.time || log.timestamp || null;

  return {
    id: `app:${log.log_id ?? log.id ?? idx}`,
    log_id: log.log_id ?? log.id ?? null,
    kind: "app",
    exercise_name: log.exercise_name || log.name || "Exercise",
    duration_minutes: Number(duration) || 0,
    calories_burned: Number(calories) || 0,
    started_at: startedAt,
    source_label: log.source || log.exercise_type || "app",
    is_favorite: !!log.is_favorite,
    raw: log,
  };
}

function normalizeWearableLog(w, idx = 0) {
  const duration = w.duration_minutes ?? w.duration ?? w.minutes ?? w.durationMin ?? w.durationMinutes ?? 0;
  const calories = w.calories_burned ?? w.calories ?? w.kcal ?? w.activeCalories ?? w.energyKcal ?? 0;
  const startedAt = w.started_at || w.start_time || w.startTime || w.timestamp || null;

  const name =
    w.exercise_name ||
    w.activity_name ||
    w.activityName ||
    w.type ||
    w.activityType ||
    "Wearable Activity";

  const wearableId = w.id || w.session_id || w.record_id || w.uuid || idx;

  return {
    id: `wearable:${wearableId}`,
    log_id: null,
    kind: "wearable",
    exercise_name: name,
    duration_minutes: Number(duration) || 0,
    calories_burned: Number(calories) || 0,
    started_at: startedAt,
    source_label: w.provider || w.source || w.device || "wearable",
    raw: w,
  };
}

function signature(x) {
  const name = String(x.exercise_name || "").toLowerCase().trim();
  const dur = Math.round(Number(x.duration_minutes) || 0);
  const cal = Math.round(Number(x.calories_burned) || 0);
  const t = x.started_at ? String(x.started_at).slice(0, 16) : "";
  return `${name}|${dur}|${cal}|${t}`;
}

function parseExerciseVoice(text) {
  const raw = String(text || "").trim();
  if (!raw) {
    return {
      exercise_name: "",
      duration_minutes: "",
      calories_burned: "",
    };
  }

  const lower = raw.toLowerCase();

  const durationMatch =
    lower.match(/(\d+(?:\.\d+)?)\s*(?:minutes|minute|mins|min)\b/) ||
    lower.match(/\bfor\s+(\d+(?:\.\d+)?)\b/);

  const caloriesMatch =
    lower.match(/(\d+(?:\.\d+)?)\s*(?:calories|calorie|kcal|cals)\b/) ||
    lower.match(/\bburn(?:ed)?\s+(\d+(?:\.\d+)?)/);

  let exerciseName = raw
    .replace(/(\d+(?:\.\d+)?)\s*(minutes|minute|mins|min)\b/gi, "")
    .replace(/(\d+(?:\.\d+)?)\s*(calories|calorie|kcal|cals)\b/gi, "")
    .replace(/\bburn(?:ed)?\s+(\d+(?:\.\d+)?)/gi, "")
    .replace(/\bfor\s+\d+(?:\.\d+)?\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!exerciseName) exerciseName = raw;

  return {
    exercise_name: exerciseName,
    duration_minutes: durationMatch ? String(Math.round(Number(durationMatch[1]) || 0)) : "",
    calories_burned: caloriesMatch ? String(Math.round(Number(caloriesMatch[1]) || 0)) : "",
  };
}


function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function fmtNum(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function opLabel(op) {
  if (!op) return "";
  const v = String(op).toLowerCase();
  if (v === "lt") return "<";
  if (v === "gt") return ">";
  if (v === "eq") return "=";
  return String(op);
}

function metricProgressPct(rule, actual) {
  if (!rule) return null;

  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  const target = Number(rule.value ?? rule.target ?? rule.threshold);
  if (!Number.isFinite(target) || target <= 0) return null;

  const a = actual == null ? 0 : Number(actual);
  if (!Number.isFinite(a)) return null;

  const MIN_SLIVER = 4;
  const normalized = clamp((a / target) * 100, 0, 100);

  if (op === "gt") return normalized === 0 ? MIN_SLIVER : Math.max(MIN_SLIVER, normalized);
  if (op === "lt") {
    const base = normalized === 0 ? MIN_SLIVER : Math.max(MIN_SLIVER, normalized);
    return a > target ? 100 : base;
  }
  if (op === "eq") return a === target ? 100 : MIN_SLIVER;

  return null;
}

function metricProgressText(rule, actual) {
  if (!rule) return "";
  const op = String(rule.operator ?? rule.op ?? "").toLowerCase();
  const target = Number(rule.value ?? rule.target ?? rule.threshold);
  const a = actual == null ? null : Number(actual);

  if (!Number.isFinite(target)) return "";

  if (op === "lt") {
    if (!Number.isFinite(a)) return `Stay under ${fmtNum(target)}`;
    const remaining = target - a;
    return remaining >= 0 ? `${fmtNum(remaining)} below limit` : `${fmtNum(Math.abs(remaining))} over limit`;
  }

  if (op === "gt") {
    if (!Number.isFinite(a)) return `Reach ${fmtNum(target)}`;
    const remaining = target - a;
    return remaining <= 0 ? `${fmtNum(Math.abs(remaining))} above goal` : `${fmtNum(remaining)} to goal`;
  }

  if (op === "eq") {
    if (!Number.isFinite(a)) return `Target: ${fmtNum(target)}`;
    return a === target ? "On target" : `Target: ${fmtNum(target)}`;
  }

  return "";
}

function getMetricVisual(rule, actual) {
  const pct = metricProgressPct(rule, actual);
  if (pct == null) return null;

  const op = String(rule?.operator ?? rule?.op ?? "").toLowerCase();
  const target = Number(rule?.value ?? rule?.target ?? rule?.threshold);
  const a = actual == null ? 0 : Number(actual);

  if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(a)) {
    return { pct, color: "#64748b", state: "neutral" };
  }

  const ratio = clamp(a / target, 0, 1);

  if (op === "gt") {
    if (a >= target) return { pct, color: "#22c55e", state: "met" };
    if (ratio >= 0.5) return { pct, color: "#f59e0b", state: "mid" };
    return { pct, color: "#ef4444", state: "low" };
  }

  if (op === "lt") {
    if (a > target) return { pct, color: "#ef4444", state: "over" };
    if (ratio >= 0.5) return { pct, color: "#f59e0b", state: "mid" };
    return { pct, color: "#22c55e", state: "good" };
  }

  if (op === "eq") {
    return { pct, color: a === target ? "#22c55e" : "#f59e0b", state: a === target ? "met" : "mid" };
  }

  return { pct, color: "#64748b", state: "neutral" };
}

function splitMetricKey(metricKey) {
  const parts = String(metricKey || "").split(".");
  return {
    domain: parts[0] || "",
    field: parts.slice(1).join(".") || "",
  };
}

function metricKeyFromRule(rule) {
  const domain = String(rule?.domain || "").trim();
  const field = String(rule?.field || "").trim();
  return domain && field ? `${domain}.${field}` : "";
}

const EXERCISE_HABIT_METRIC_OPTIONS = [
  { value: "wearables.steps", label: "Steps" },
  { value: "wearables.calories_burned", label: "Wearable Calories Burned" },
  { value: "wearables.active_minutes", label: "Active Minutes" },
  { value: "app.exercise_minutes", label: "Exercise Minutes" },
  { value: "app.exercise_calories_burned", label: "Exercise Calories Burned" },
];

const EXERCISE_METRIC_KEYS = new Set(EXERCISE_HABIT_METRIC_OPTIONS.map((x) => x.value));

function isExerciseHabitRecord(habit) {
  const category = String(habit?.category || habit?.pillar || habit?.group || habit?.area || "").toLowerCase();
  if (["exercise", "fitness", "workout", "training", "movement"].includes(category)) return true;

  if (habit?.metric_rule) {
    const key = metricKeyFromRule(habit.metric_rule);
    return EXERCISE_METRIC_KEYS.has(key);
  }

  return false;
}

function HabitRing({ rule, actual, label }) {
  const visual = getMetricVisual(rule, actual);
  const size = 106;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = visual?.pct ?? 0;
  const dash = circumference * (pct / 100);
  const target = Number(rule?.value ?? rule?.target ?? rule?.threshold);

  return (
    <div className="tracker-habit-ring" aria-label={label || "exercise habit progress"}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="tracker-habit-ring-svg">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={visual?.color || "#f59e0b"}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="tracker-habit-ring-center">
        <div className="tracker-habit-ring-value">{actual == null ? "—" : fmtNum(actual)}</div>
        <div className="tracker-habit-ring-target">{opLabel(rule?.operator || rule?.op)} {fmtNum(target)}</div>
      </div>
    </div>
  );
}


function useIsMobileExercise(breakpoint = 809) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [breakpoint]);

  return isMobile;
}

function ExerciseMobileCarousel({ children, className = "", slideClassName = "" }) {
  const isMobile = useIsMobileExercise();
  const trackRef = useRef(null);
  const items = React.Children.toArray(children).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);

  const getSlides = useCallback(() => {
    const el = trackRef.current;
    if (!el) return [];
    return Array.from(el.children);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    const el = trackRef.current;
    if (!el) return;

    if (!isMobile) {
      el.scrollLeft = 0;
      return;
    }

    const slides = getSlides();
    if (!slides.length) {
      el.scrollLeft = 0;
      return;
    }

    const first = slides[0];
    const left = first.offsetLeft - (el.clientWidth - first.offsetWidth) / 2;
    el.scrollLeft = Math.max(0, left);
  }, [items.length, isMobile, getSlides]);

  const syncIndex = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const slides = getSlides();
    if (!slides.length) return;

    const viewportCenter = el.scrollLeft + el.clientWidth / 2;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    setActiveIndex(clamp(bestIndex, 0, Math.max(items.length - 1, 0)));
  }, [items.length, getSlides]);

  const scrollToIndex = useCallback(
    (index) => {
      const el = trackRef.current;
      if (!el) return;

      const slides = getSlides();
      if (!slides.length) return;

      const next = clamp(index, 0, Math.max(items.length - 1, 0));
      const target = slides[next];
      if (!target) return;

      const left = target.offsetLeft - (el.clientWidth - target.offsetWidth) / 2;
      el.scrollTo({ left, behavior: "smooth" });
      setActiveIndex(next);
    },
    [getSlides, items.length]
  );

  if (!isMobile) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div>
    <div className={`exercise-mobile-carousel-shell ${className}`.trim()}>
      <div
        ref={trackRef}
        className="exercise-mobile-carousel-track"
        onScroll={syncIndex}
      >
        {items.map((child, index) => (
          <div
            key={index}
            className={`exercise-mobile-carousel-slide ${slideClassName}`.trim()}
            onClick={() => {
              if (items.length <= 1) return;
              scrollToIndex(activeIndex >= items.length - 1 ? 0 : activeIndex + 1);
            }}
          >
            {child}
          </div>
        ))}
      </div>
</div>
      {items.length > 1 ? (
        <div
          className="exercise-mobile-carousel-dots"
          role="tablist"
          aria-label="Carousel pages"
        >
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`exercise-mobile-carousel-dot ${index === activeIndex ? "is-active" : ""}`}
              aria-label={`Go to slide ${index + 1}`}
              aria-selected={index === activeIndex}
              onClick={(e) => {
                e.stopPropagation();
                scrollToIndex(index);
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}


export default function ExerciseTracker({
  exerciseLogs,
  predefinedExercises,
  onLogExercise,
  onDeleteLog,
  onCalculateCalories,
  token,
  onOpenLogChoice,
  fileInputRef,
  openManualTrigger,
  openPredefinedTrigger,
  openMachineTrigger,
  openVoiceTrigger,
  userId,
  onNavigate,
  onStartWorkout,
  showToast,
}) {
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  const localLegacyRef = useRef(null);
  const legacyRefToUse = fileInputRef ?? localLegacyRef;

  const [mode, setMode] = useState(null);

  const prevManualTriggerRef = useRef(openManualTrigger);
  const prevPredefinedTriggerRef = useRef(openPredefinedTrigger);
  const prevMachineTriggerRef = useRef(openMachineTrigger);
  const prevVoiceTriggerRef = useRef(openVoiceTrigger);

  const [favBusy, setFavBusy] = useState(false);

  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [dateLogs, setDateLogs] = useState(exerciseLogs || []);
  const [dateLoading, setDateLoading] = useState(false);

  const [wearableLogs, setWearableLogs] = useState([]);
  const [wearableLoading, setWearableLoading] = useState(false);

  const [wearableSummary, setWearableSummary] = useState(null);
  const [wearableSummaryLoading, setWearableSummaryLoading] = useState(false);
  const [todayWorkoutItems, setTodayWorkoutItems] = useState([]);
  const [todayWorkoutLoading, setTodayWorkoutLoading] = useState(false);
  const [machineError, setMachineError] = useState("");
  const [machineErrorCode, setMachineErrorCode] = useState("");

  const [voiceText, setVoiceText] = useState("");
  const [voiceListening, setVoiceListening] = useState(false);
  const recognitionRef = useRef(null);

  const [exerciseHabits, setExerciseHabits] = useState([]);
  const [exerciseHabitsLoading, setExerciseHabitsLoading] = useState(false);
  const [habitSavingId, setHabitSavingId] = useState("");
  const [showHabitModal, setShowHabitModal] = useState(false);
  const [habitCreateBusy, setHabitCreateBusy] = useState(false);
  const [habitForm, setHabitForm] = useState({
    name: "",
    description: "",
    habit_type: "metric",
    time_of_day: "18:00",
    metric_key: "wearables.steps",
    operator: "gt",
    value: "",
  });

  useEffect(() => {
    if (selectedDate === todayISO()) {
      setDateLogs(exerciseLogs || []);
    }
  }, [exerciseLogs, selectedDate]);

  const loadAppLogsForDate = async (dateStr) => {
    if (!token || !userId) return;
    setDateLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/exercise/logs/${userId}?date=${encodeURIComponent(dateStr)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load exercise logs");
      }

      const data = await res.json();
      setDateLogs(data.logs || []);
    } catch (e) {
      console.error(e);
      showToast?.(`Error loading exercises: ${e.message}`, "error");
      setDateLogs([]);
    } finally {
      setDateLoading(false);
    }
  };

  const loadWearableForDate = async (dateStr) => {
    if (!token) return;
    setWearableLoading(true);
    try {
      const endpoint = `${BACKEND_URL}/api/wearables/synced-data/day?date=${encodeURIComponent(dateStr)}`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load wearable exercise");
      }

      const data = await res.json();

      const workouts =
        data?.merged?.workouts ||
        data?.rows?.flatMap((r) => (Array.isArray(r.workouts) ? r.workouts : [])) ||
        [];

      setWearableLogs(Array.isArray(workouts) ? workouts : []);
    } catch (e) {
      console.warn("[ExerciseTracker] wearable exercise load:", e.message);
      setWearableLogs([]);
    } finally {
      setWearableLoading(false);
    }
  };

  const loadWearableSummaryForDate = async (dateStr) => {
    if (!token || !userId) return;
    setWearableSummaryLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/dashboard/${encodeURIComponent(userId)}?date=${encodeURIComponent(dateStr)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load dashboard wearable summary");
      }

      const dash = await res.json();
      const w = dash?.wearable || null;

      setWearableSummary(
        w
          ? {
              source: w.source || null,
              steps: w.steps ?? null,
              active_minutes: w.active_minutes ?? null,
              calories_burned: w.calories_burned ?? null,
              sleep_minutes: w.sleep_minutes ?? null,
            }
          : null
      );
    } catch (e) {
      console.warn("[ExerciseTracker] wearable summary load:", e.message);
      setWearableSummary(null);
    } finally {
      setWearableSummaryLoading(false);
    }
  };

  const loadTodayWorkoutItems = async (dateStr) => {
    if (!token || !userId) return;
    setTodayWorkoutLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/workouts/program/day?date=${encodeURIComponent(dateStr)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load program workouts");
      }

      const data = await res.json();
      setTodayWorkoutItems(Array.isArray(data?.workouts) ? data.workouts : []);
    } catch (e) {
      console.warn("[ExerciseTracker] program workout load:", e.message);
      setTodayWorkoutItems([]);
    } finally {
      setTodayWorkoutLoading(false);
    }
  };

const openWorkoutSession = (eventId) => {
  if (!eventId) return;

  if (onStartWorkout) {
    onStartWorkout(eventId);
    return;
  }

  onNavigate?.("workouts");
};

  useEffect(() => {
    if (selectedDate === todayISO()) setDateLogs(exerciseLogs || []);
    else loadAppLogsForDate(selectedDate);

    loadWearableForDate(selectedDate);
    loadWearableSummaryForDate(selectedDate);
    loadTodayWorkoutItems(selectedDate);
  }, [selectedDate, exerciseLogs]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadExerciseHabits = useCallback(async (dateStr) => {
  if (!token) return;

  setExerciseHabitsLoading(true);

  try {
    const [todayRes, allRes] = await Promise.all([
      fetch(
        `${BACKEND_URL}/api/habits/today?date=${encodeURIComponent(dateStr)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        }
      ),
      fetch(
        `${BACKEND_URL}/api/habits?user_id=me`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        }
      ),
    ]);

    if (!todayRes.ok) throw new Error(await todayRes.text());
    if (!allRes.ok) throw new Error(await allRes.text());

    const todayOut = await todayRes.json();
    const allOut = await allRes.json();

    const todayHabits = Array.isArray(todayOut?.habits) ? todayOut.habits : [];
    const allHabits = Array.isArray(allOut?.habits) ? allOut.habits : [];

    const merged = new Map();

    for (const h of allHabits) {
      if (!h?.habit_id) continue;
      merged.set(String(h.habit_id), h);
    }

    for (const h of todayHabits) {
      if (!h?.habit_id) continue;
      const existing = merged.get(String(h.habit_id)) || {};
      merged.set(String(h.habit_id), { ...existing, ...h });
    }

    const filtered = Array.from(merged.values()).filter(isExerciseHabitRecord);

    setExerciseHabits(filtered);
  } catch (e) {
    console.warn("[ExerciseTracker] habits load:", e.message);
    setExerciseHabits([]);
  } finally {
    setExerciseHabitsLoading(false);
  }
}, [token]);

  useEffect(() => {
    loadExerciseHabits(selectedDate);
  }, [selectedDate, loadExerciseHabits]);

  const combinedLogs = useMemo(() => {
    const appNorm = (dateLogs || []).map(normalizeAppLog);
    const wearNorm = (wearableLogs || []).map(normalizeWearableLog);

    const seen = new Set();
    const out = [];

    for (const x of appNorm) {
      const sig = signature(x);
      if (seen.has(sig)) continue;
      seen.add(sig);
      out.push(x);
    }

    for (const x of wearNorm) {
      const sig = signature(x);
      if (seen.has(sig)) continue;
      seen.add(sig);
      out.push(x);
    }

    out.sort((a, b) => {
      const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
      const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
      return tb - ta;
    });

    return out;
  }, [dateLogs, wearableLogs]);

  const quickAddExerciseOptions = useMemo(() => {
    const previousLogged = (combinedLogs || [])
      .filter((x) => x.kind === "app")
      .map((x) => {
        const dur = Number(x.duration_minutes) || 0;
        const cal = Number(x.calories_burned) || 0;
        const rate = dur > 0 ? Math.round((cal / dur) * 100) / 100 : 0;

        return {
          name: x.exercise_name,
          calories_per_minute: rate,
          default_duration_minutes: dur || 30,
          source: "history",
        };
      });

    const uniqueMap = new Map();
    previousLogged.forEach((item) => {
      const key = String(item.name || "").trim().toLowerCase();
      if (!key || uniqueMap.has(key)) return;
      uniqueMap.set(key, item);
    });

    const dedupedPrevious = Array.from(uniqueMap.values());

    if (dedupedPrevious.length > 0) {
      return dedupedPrevious;
    }

    return (predefinedExercises || []).map((ex) => ({
      name: ex.name,
      calories_per_minute: Number(ex.calories_per_minute) || 0,
      default_duration_minutes: Number(ex.default_duration_minutes) || 30,
      source: "db",
    }));
  }, [combinedLogs, predefinedExercises]);

  const isAnyLoading = dateLoading || wearableLoading || wearableSummaryLoading;

  const [analyzing, setAnalyzing] = useState(false);
  const [machineData, setMachineData] = useState(null);
  const [machineImage, setMachineImage] = useState(null);
  const [machineDetails, setMachineDetails] = useState("");

  const [preExercise, setPreExercise] = useState("");
  const [preDuration, setPreDuration] = useState("");

  const preCalories = useMemo(() => {
    if (!preExercise || !preDuration) return "";
    const dur = parseInt(preDuration, 10);
    if (!Number.isFinite(dur) || dur <= 0) return "";

    const selected = quickAddExerciseOptions.find((x) => x.name === preExercise);
    if (!selected) return "";

    return String(Math.round((Number(selected.calories_per_minute) || 0) * dur));
  }, [preExercise, preDuration, quickAddExerciseOptions]);

  const [manName, setManName] = useState("");
  const [manDuration, setManDuration] = useState("");
  const [manCalories, setManCalories] = useState("");
  const [manWeight, setManWeight] = useState("");
  const [estimatingCalories, setEstimatingCalories] = useState(false);


  useEffect(() => {
    if (prevMachineTriggerRef.current !== openMachineTrigger) {
      setMode("machine");
      setMachineData(null);
      setMachineImage(null);
      setAnalyzing(false);
      prevMachineTriggerRef.current = openMachineTrigger;
    }
  }, [openMachineTrigger]);

  useEffect(() => {
    if (prevPredefinedTriggerRef.current !== openPredefinedTrigger) {
      setMode("predefined");
      prevPredefinedTriggerRef.current = openPredefinedTrigger;
    }
  }, [openPredefinedTrigger]);

  useEffect(() => {
    if (prevManualTriggerRef.current !== openManualTrigger) {
      setMode("manual");
      prevManualTriggerRef.current = openManualTrigger;
    }
  }, [openManualTrigger]);

  useEffect(() => {
    if (prevVoiceTriggerRef.current !== openVoiceTrigger) {
      setVoiceText("");
      setMode("voice");
      prevVoiceTriggerRef.current = openVoiceTrigger;
    }
  }, [openVoiceTrigger]);

  const closePanel = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
    }
    setVoiceListening(false);
    setMode(null);
    setAnalyzing(false);
    setMachineData(null);
    setMachineImage(null);
    setMachineError("");
    setMachineErrorCode("");
    setManWeight("");
    setEstimatingCalories(false);
  };

  const addExerciseFavorite = async ({ exercise_name, calories_per_minute, default_duration_minutes }) => {
    if (!userId) return;
    if (!token) {
      showToast?.("Missing auth token. Please log in again.", "error");
      return;
    }

    const payload = {
      user_id: userId,
      exercise_name,
      calories_per_minute: calories_per_minute ?? null,
      default_duration_minutes: default_duration_minutes ?? 30,
    };

    setFavBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/exercise/favorites/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to save favorite");
      }

      showToast?.("Saved exercise to favorites!", "success");
    } catch (e) {
      showToast?.(`Error saving favorite: ${e.message}`, "error");
    } finally {
      setFavBusy(false);
    }
  };

  const openMachineCamera = () => {
    setMode("machine");
    setMachineData(null);
    setMachineImage(null);
    setAnalyzing(false);
    setTimeout(() => cameraInputRef.current?.click(), 0);
  };

  const openMachineUpload = () => {
    setMode("machine");
    setMachineData(null);
    setMachineImage(null);
    setAnalyzing(false);
    setTimeout(() => uploadInputRef.current?.click(), 0);
  };

  const handleMachineImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setMode("machine");
    setAnalyzing(true);
    setMachineData(null);
    setMachineError("");
    setMachineErrorCode("");

    const reader = new FileReader();
    reader.onload = (ev) => setMachineImage(ev.target.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append("file", file);
    if (machineDetails && machineDetails.trim()) {
      formData.append("details", machineDetails.trim());
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/exercise/analyze-machine`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const rawText = await response.text();
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        const backendMessage = data?.error || data?.detail || rawText || "Could not analyze image";
        throw new Error(backendMessage);
      }

      if (data?.success && data?.data) {
        setMachineData(data.data);
        setMachineError("");
        setMachineErrorCode("");
        return;
      }

      const fallbackMsg = data?.error || "Could not analyze image";
      setMachineError(fallbackMsg);
      setMachineErrorCode("parse_failed");
      showToast?.(fallbackMsg, "error");
    } catch (error) {
      const msg = error?.message || "Could not analyze image";
      setMachineError(msg);
      setMachineErrorCode(
        /heic|heif|unsupported/i.test(msg)
          ? "unsupported_format"
          : /parse|analy/i.test(msg)
          ? "parse_failed"
          : "upload_failed"
      );
      showToast?.(msg, "error");
    } finally {
      setAnalyzing(false);
    }
  };

  const logMachineExercise = (e) => {
    e.preventDefault();
    if (!machineData) return;

    const fd = new FormData(e.currentTarget);

    const exerciseData = {
      exercise_name: fd.get("exercise_name") || machineData.exercise_name || "Machine Workout",
      duration_minutes: Number(fd.get("duration")) || Number(machineData.duration_minutes) || 30,
      calories_burned: Number(fd.get("calories_burned")) || Number(machineData.calories_burned) || 0,
      exercise_type: "machine",
      notes: fd.get("notes") || "",
      date: selectedDate,
    };

    onLogExercise(exerciseData, e.currentTarget);

    setMachineData(null);
    setMachineImage(null);
    setMachineDetails("");
    setMode(null);
    showToast?.("Exercise logged.", "success");

    if (selectedDate !== todayISO()) loadAppLogsForDate(selectedDate);
    loadWearableForDate(selectedDate);
    loadWearableSummaryForDate(selectedDate);
    loadTodayWorkoutItems(selectedDate);
  };

  const submitPredefined = (e) => {
    e.preventDefault();

    const dur = Number(preDuration);
    const cal = Number(preCalories);

    const exerciseData = {
      exercise_name: preExercise,
      duration_minutes: Number.isFinite(dur) ? dur : 0,
      calories_burned: Number.isFinite(cal) ? cal : 0,
      exercise_type: "predefined",
      notes: "",
      date: selectedDate,
    };

    onLogExercise(exerciseData, e.currentTarget);

    setPreExercise("");
    setPreDuration("");
    setMode(null);
    showToast?.("Exercise logged.", "success");

    if (selectedDate !== todayISO()) loadAppLogsForDate(selectedDate);
    loadWearableForDate(selectedDate);
    loadWearableSummaryForDate(selectedDate);
    loadTodayWorkoutItems(selectedDate);
  };
  const estimateManualCalories = async () => {
    const exerciseName = String(manName || "").trim();
    const duration = Number(manDuration);
    const weight = Number(manWeight);

    if (!exerciseName) {
      showToast?.("Enter a workout type first.", "error");
      return;
    }

    if (!Number.isFinite(duration) || duration <= 0) {
      showToast?.("Enter duration in minutes before estimating.", "error");
      return;
    }

    if (!Number.isFinite(weight) || weight <= 0) {
      showToast?.("Enter your weight before estimating calories.", "error");
      return;
    }

    setEstimatingCalories(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/exercise/estimate-calories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          exercise_name: exerciseName,
          duration_minutes: duration,
          weight_lbs: weight,
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || data?.error || "Failed to estimate calories");
      }

      const estimated = Number(data?.calories_burned);
      if (!Number.isFinite(estimated) || estimated <= 0) {
        throw new Error("Estimate came back empty");
      }

      setManCalories(String(Math.round(estimated)));
      showToast?.(data?.source === "ai" ? "AI calorie estimate added." : "Calorie estimate added.", "success");
    } catch (e) {
      showToast?.(`Error estimating calories: ${e.message}`, "error");
    } finally {
      setEstimatingCalories(false);
    }
  };

  const submitManual = (e) => {
    e.preventDefault();

    const dur = Number(manDuration);
    const cal = Number(manCalories);

    const exerciseData = {
      exercise_name: manName,
      duration_minutes: Number.isFinite(dur) ? dur : 0,
      calories_burned: Number.isFinite(cal) ? cal : 0,
      exercise_type: "manual",
      notes: "",
      date: selectedDate,
    };

    onLogExercise(exerciseData, e.currentTarget);

    setManName("");
    setManDuration("");
    setManCalories("");
    setManWeight("");
    setMode(null);
    showToast?.("Exercise logged.", "success");

    if (selectedDate !== todayISO()) loadAppLogsForDate(selectedDate);
    loadWearableForDate(selectedDate);
    loadWearableSummaryForDate(selectedDate);
    loadTodayWorkoutItems(selectedDate);
  };

  async function ensureMicPermission() {
    const status = await navigator.permissions?.query?.({ name: "microphone" }).catch(() => null);

    if (status?.state === "granted") return true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch {
      return false;
    }
  }
  const startVoiceListening = async () => {
    const allowed = await ensureMicPermission();
    if (!allowed) {
      showToast?.("Microphone permission is required for voice logging.", "error");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showToast?.("Voice recognition is not supported in this browser. Type your log instead.", "warning");
      return;
    }

    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch {}

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;

    recognition.onstart = () => setVoiceListening(true);
    recognition.onend = () => setVoiceListening(false);
    recognition.onerror = () => {
      setVoiceListening(false);
      showToast?.("Voice capture failed. You can still type the exercise below.", "warning");
    };
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      setVoiceText(transcript.trim());
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopVoiceListening = () => {
    try {
      recognitionRef.current?.stop();
    } catch {}
    setVoiceListening(false);
  };

  const applyVoiceExercise = () => {
    const parsed = parseExerciseVoice(voiceText);

    if (!parsed.exercise_name.trim()) {
      showToast?.("Say or type an exercise first.", "error");
      return;
    }

    setManName(parsed.exercise_name);
    if (parsed.duration_minutes) setManDuration(parsed.duration_minutes);
    if (parsed.calories_burned) setManCalories(parsed.calories_burned);

    setMode("manual");
    showToast?.("Voice entry moved into manual form.", "success");
  };

  const totals = useMemo(() => {
    const totalCalories = combinedLogs.reduce((s, x) => s + (Number(x.calories_burned) || 0), 0);
    const totalMinutes = combinedLogs.reduce((s, x) => s + (Number(x.duration_minutes) || 0), 0);
    const wearableSteps = wearableSummary?.steps ?? null;
    const wearableActiveMin = wearableSummary?.active_minutes ?? null;

    return { totalCalories, totalMinutes, wearableSteps, wearableActiveMin };
  }, [combinedLogs, wearableSummary]);

  

  const toggleHabitComplete = async (habitId, checked) => {
    if (!token || !habitId || !selectedDate) return;
    setHabitSavingId(String(habitId));
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/${habitId}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ habit_id: habitId, date: selectedDate, checked: !!checked }),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadExerciseHabits(selectedDate);
    } catch (e) {
      showToast?.(`Error updating habit: ${e?.message || String(e)}`, "error");
    } finally {
      setHabitSavingId("");
    }
  };

  const createExerciseHabit = async (e) => {
    e.preventDefault();
    if (!token) return;

    setHabitCreateBusy(true);
    try {
      const payload = {
        name: habitForm.name.trim(),
        description: habitForm.description.trim(),
        category: "fitness",
        habit_type: habitForm.habit_type,
        time_of_day: habitForm.time_of_day || "18:00",
        start_date: selectedDate,
      };

      if (habitForm.habit_type === "metric") {
        const { domain, field } = splitMetricKey(habitForm.metric_key);
        payload.metric_rule = {
          domain,
          field,
          operator: habitForm.operator,
          value: Number(habitForm.value) || 0,
        };
      }

      const res = await fetch(`${BACKEND_URL}/api/habits`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());

      setHabitForm({
        name: "",
        description: "",
        habit_type: "metric",
        time_of_day: "18:00",
        metric_key: "wearables.steps",
        operator: "gt",
        value: "",
      });
      setShowHabitModal(false);
      await loadExerciseHabits(selectedDate);
      showToast?.("Exercise habit created.", "success");
    } catch (e2) {
      showToast?.(`Error creating habit: ${e2?.message || String(e2)}`, "error");
    } finally {
      setHabitCreateBusy(false);
    }
  };



  return (
  <div className="exercise-view">
    <div className="tracker-hero tracker-hero--exercise">
      <div className="tracker-hero-main tracker-hero-main--exercise">
        <div className="tracker-hero-copy tracker-hero-copy--exercise">
          <div className="tracker-hero-heading-row">
            <h1 className="heading-1" style={{ margin: 0 }}>Exercise Tracker</h1>
            <button
              type="button"
              className="btn-primary tracker-hero-add-btn"
              onClick={() => onOpenLogChoice?.("exercise")}
              data-testid="exercise-open-choice"
            >
              ➕ Add Exercise
            </button>
          </div>
          <div className="body-small exercise-mobile-hide-subheader" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
            Log workouts, quick add exercises, and follow your program.
          </div>
        </div>

        <div className="exercise-date-toolbar">
          <div className="date-nav-inline exercise-date-nav-row">
            <button
              type="button"
              className="btn-secondary date-nav-btn exercise-date-arrow"
              onClick={() => setSelectedDate(addDaysISO(selectedDate, -1))}
              aria-label="Previous day"
              title="Previous day"
            >
              ◀
            </button>

            <input
              type="date"
              className="form-input date-nav-date exercise-date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              aria-label="Select date"
            />

            <button
              type="button"
              className="btn-secondary date-nav-btn exercise-date-arrow"
              onClick={() => setSelectedDate(addDaysISO(selectedDate, 1))}
              aria-label="Next day"
              title="Next day"
            >
              ▶
            </button>
          </div>

          <div className="exercise-date-actions">
            {selectedDate !== todayISO() && (
              <button
                type="button"
                className="btn-secondary exercise-date-action-btn"
                onClick={() => setSelectedDate(todayISO())}
              >
                Today
              </button>
            )}
          </div>
        </div>
      </div>
    </div>

    {mode && (
      <div className="tracker-primary-actions tracker-primary-actions--exercise">
        <button type="button" className="btn-secondary" onClick={closePanel}>
          Close
        </button>
      </div>
    )}

    <div
      className="card"
      style={{
        padding: 16,
        marginBottom: 18,
        background: "var(--bg-secondary)",
        border: "1px solid var(--border-light)",
      }}
    >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div className="heading-4" style={{ marginBottom: 4 }}>
              🏋️ Program Workout ({selectedDate === todayISO() ? "Today" : selectedDate})
            </div>
            <div className="body-small exercise-mobile-hide-subheader" style={{ color: "var(--text-secondary)" }}>
              Open the scheduled workout directly from Exercise Tracker.
            </div>
          </div>
        </div>

        {todayWorkoutLoading ? (
          <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 10 }}>
            Loading…
          </div>
        ) : todayWorkoutItems.length === 0 ? (
          <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 10 }}>
            No program workout scheduled for this date.
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <ExerciseMobileCarousel className="exercise-program-grid" slideClassName="exercise-mobile-program-slide">
              {todayWorkoutItems.map((w) => {
                const label = w.finished ? "Edit Workout" : w.started ? "Finish Workout" : "Start Workout";
                return (
                  <div
                    key={w.event_id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      padding: 14,
                      borderRadius: 12,
                      border: "1px solid var(--border-light)",
                      background: "var(--bg-card)",
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 800 }}>
                        {w.title}
                        {w.finished ? (
                          <span className="body-small" style={{ marginLeft: 8, color: "var(--text-secondary)" }}>
                            • Completed
                          </span>
                        ) : null}
                      </div>
                      <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                        {(w.start_at ? String(w.start_at).slice(11, 16) : "09:00")} •{" "}
                        {Array.isArray(w.sub_items) ? w.sub_items.length : 0} exercises
                      </div>
                    </div>

                    <button type="button" className="btn-primary" onClick={() => openWorkoutSession(w.event_id)}>
                      {label}
                    </button>
                  </div>
                );
              })}
            </ExerciseMobileCarousel>
          </div>
        )}
      </div>

      


      <div className="form-section" style={{ marginTop: 16, marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h3 className="heading-3" style={{ marginBottom: 6 }}>Today's Exercise Totals</h3>
            <div className="body-small exercise-mobile-hide-subheader" style={{ color: "var(--text-secondary)" }}>
              Totals for {selectedDate === todayISO() ? "today" : selectedDate}.
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <ExerciseMobileCarousel className="exercise-totals-grid" slideClassName="exercise-mobile-summary-slide">
            {[
              { label: "Workouts", value: String(combinedLogs.length), unit: "logged" },
              { label: "Exercise Minutes", value: String(Math.round(totals.totalMinutes)), unit: "min" },
              { label: "Calories Burned", value: String(Math.round(totals.totalCalories)), unit: "cal" },
              { label: "Steps", value: totals.wearableSteps != null ? Number(totals.wearableSteps).toLocaleString() : "—", unit: "steps" },
              { label: "Active Minutes", value: totals.wearableActiveMin != null ? String(totals.wearableActiveMin) : "—", unit: "min" },
            ].map((item) => (
              <div
                key={item.label}
                className="meal-card exercise-summary-card"
                style={{
                  padding: 14,
                  border: "1px solid var(--border-light)",
                  background: "var(--bg-secondary)",
                }}
              >
                <div className="body-small" style={{ color: "var(--text-secondary)", marginBottom: 6 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>
                  {item.value}
                </div>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 4 }}>
                  {item.unit}
                </div>
              </div>
            ))}
          </ExerciseMobileCarousel>
        </div>
      </div>

      <div
        className="card"
        style={{
          padding: 16,
          marginBottom: 18,
          background: "var(--bg-secondary)",
          border: "1px solid var(--border-light)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="heading-4" style={{ marginBottom: 4 }}>
              ⌚ Wearables (Selected day)
            </div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>
              {wearableSummary?.source ? `Source: ${wearableSummary.source}` : "No wearable data detected for this day"}
            </div>
          </div>

          <div className="exercise-wearables-summary-wrap">
            <ExerciseMobileCarousel className="exercise-wearables-summary" slideClassName="exercise-mobile-wearable-slide">
              <div className="exercise-wearable-stat" style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(255,255,255,0.06)" }}>
                <div className="body-small" style={{ color: "var(--text-muted)" }}>Steps</div>
                <div style={{ fontWeight: 800 }}>
                  {totals.wearableSteps != null ? totals.wearableSteps.toLocaleString() : "—"}
                </div>
              </div>

              <div className="exercise-wearable-stat" style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(255,255,255,0.06)" }}>
                <div className="body-small" style={{ color: "var(--text-muted)" }}>Active minutes</div>
                <div style={{ fontWeight: 800 }}>{totals.wearableActiveMin != null ? totals.wearableActiveMin : "—"}</div>
              </div>

              <div className="exercise-wearable-stat" style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(255,255,255,0.06)" }}>
                <div className="body-small" style={{ color: "var(--text-muted)" }}>Exercise mins (logged)</div>
                <div style={{ fontWeight: 800 }}>{Math.round(totals.totalMinutes)}</div>
              </div>

              <div className="exercise-wearable-stat" style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(255,255,255,0.06)" }}>
                <div className="body-small" style={{ color: "var(--text-muted)" }}>Calories (logged)</div>
                <div style={{ fontWeight: 800 }}>{Math.round(totals.totalCalories)}</div>
              </div>
            </ExerciseMobileCarousel>
          </div>
        </div>
      </div>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,.heic,.heif,image/heic,image/heif"
        capture="environment"
        onChange={handleMachineImageUpload}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*,.heic,.heif,image/heic,image/heif"
        onChange={handleMachineImageUpload}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <input
        ref={legacyRefToUse}
        type="file"
        accept="image/*,.heic,.heif,image/heic,image/heif"
        onChange={handleMachineImageUpload}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />


      {showHabitModal && (
        <div className="modal-overlay" onMouseDown={() => setShowHabitModal(false)}>
          <div
            className="modal-content ct-form-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(700px, 100%)", maxWidth: 700 }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <h2 className="heading-3" style={{ margin: 0 }}>New Exercise Habit</h2>
              <button type="button" className="btn-secondary" onClick={() => setShowHabitModal(false)}>✕</button>
            </div>

            <form onSubmit={createExerciseHabit} className="tracker-habit-form">
              <div className="form-group">
                <label className="body-medium">Habit Name</label>
                <input
                  className="form-input"
                  value={habitForm.name}
                  onChange={(e) => setHabitForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="body-medium">Description</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={habitForm.description}
                  onChange={(e) => setHabitForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="tracker-habit-grid">
                <div className="form-group">
                  <label className="body-medium">Type</label>
                  <select
                    className="form-input"
                    value={habitForm.habit_type}
                    onChange={(e) => setHabitForm((p) => ({ ...p, habit_type: e.target.value }))}
                  >
                    <option value="metric">Metric</option>
                    <option value="checkbox">Checkbox</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="body-medium">Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={habitForm.time_of_day}
                    onChange={(e) => setHabitForm((p) => ({ ...p, time_of_day: e.target.value }))}
                  />
                </div>
              </div>

              {habitForm.habit_type === "metric" ? (
                <>
                  <div className="form-group">
                    <label className="body-medium">Metric</label>
                    <select
                      className="form-input"
                      value={habitForm.metric_key}
                      onChange={(e) => setHabitForm((p) => ({ ...p, metric_key: e.target.value }))}
                    >
                      {EXERCISE_HABIT_METRIC_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="tracker-habit-grid">
                    <div className="form-group">
                      <label className="body-medium">Operator</label>
                      <select
                        className="form-input"
                        value={habitForm.operator}
                        onChange={(e) => setHabitForm((p) => ({ ...p, operator: e.target.value }))}
                      >
                        <option value="gt">{">"} Greater than</option>
                        <option value="lt">{"<"} Less than</option>
                        <option value="eq">{"="} Equal to</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="body-medium">Target Value</label>
                      <input
                        type="number"
                        className="form-input"
                        value={habitForm.value}
                        onChange={(e) => setHabitForm((p) => ({ ...p, value: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                </>
              ) : null}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
                <button type="button" className="btn-secondary" onClick={() => setShowHabitModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={habitCreateBusy}>
                  {habitCreateBusy ? "Saving…" : "Create Habit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mode === "voice" && (
        <div className="modal-overlay" onMouseDown={closePanel}>
          <div
            className="modal-content ct-form-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(760px, 100%)", maxWidth: 760 }}
          >
            <div
              className="modal-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}
            >
              <h2 className="heading-3" style={{ margin: 0 }}>
                🎤 Voice Exercise Log
              </h2>
              <button type="button" className="btn-secondary" onClick={closePanel}>
                ✕
              </button>
            </div>

            <div className="body-small" style={{ color: "var(--text-secondary)", marginBottom: 12 }}>
              Say something like “treadmill 30 minutes 250 calories” or type it below.
            </div>

            <textarea
              className="form-input"
              rows={5}
              value={voiceText}
              onChange={(e) => setVoiceText(e.target.value)}
              placeholder="Example: elliptical 20 minutes 180 calories"
            />

            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <button type="button" className="btn-secondary" onClick={voiceListening ? stopVoiceListening : startVoiceListening}>
                {voiceListening ? "Stop Listening" : "Start Listening"}
              </button>
              <button type="button" className="btn-primary" onClick={applyVoiceExercise}>
                Use Voice Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === "predefined" && (
        <div className="modal-overlay" onMouseDown={closePanel}>
          <div
            className="modal-content ct-form-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(860px, 100%)", maxWidth: 860 }}
          >
            <div
              className="modal-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}
            >
              <h2 className="heading-3" style={{ margin: 0 }}>
                ⚡ Quick Add Exercise
              </h2>
              <button type="button" className="btn-secondary" onClick={closePanel}>
                ✕
              </button>
            </div>
            <form onSubmit={submitPredefined}>
              <div className="form-group">
                <label className="body-medium">Exercise</label>
                <select
                  value={preExercise}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPreExercise(value);

                    const selected = quickAddExerciseOptions.find((x) => x.name === value);
                    if (selected?.default_duration_minutes) {
                      setPreDuration(String(selected.default_duration_minutes));
                    }
                  }}
                  required
                  className="form-input"
                  data-testid="predefined-exercise-select"
                >
                  <option value="">
                    {quickAddExerciseOptions.some((x) => x.source === "history")
                      ? "Select from previous exercises"
                      : "Select exercise"}
                  </option>
                  {quickAddExerciseOptions.map((ex) => (
                    <option key={`${ex.source}:${ex.name}`} value={ex.name}>
                      {ex.name}
                      {ex.source === "history" ? " • previous log" : " • exercise db"}
                      {Number.isFinite(ex.calories_per_minute)
                        ? ` (${ex.calories_per_minute} cal/min)`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="body-medium">Duration (minutes)</label>
                <input
                  type="number"
                  value={preDuration}
                  onChange={(e) => setPreDuration(e.target.value)}
                  required
                  className="form-input"
                  data-testid="predefined-duration"
                />
              </div>

              <div className="form-group">
                <label className="body-medium">Calories Burned</label>
                <input
                  type="number"
                  value={preCalories}
                  readOnly
                  className="form-input"
                  data-testid="predefined-calories"
                />
              </div>

              <div className="modal-footer" style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="submit" className="btn-primary" data-testid="log-predefined-exercise">
                  Log Exercise
                </button>
              </div>

              {machineError ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid var(--border-light)",
                    background: "rgba(239,68,68,0.08)",
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 6 }}>Couldn’t parse photo</div>
                  <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                    {machineError}
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                    <button type="button" className="btn-secondary" onClick={openMachineCamera}>
                      Retry Camera
                    </button>
                    <button type="button" className="btn-secondary" onClick={openMachineUpload}>
                      Retry Upload
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        setMachineError("");
                        setMachineErrorCode("");
                        setMode("manual");
                      }}
                    >
                      Enter Manually Instead
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      )}

      {mode === "manual" && (
        <div className="modal-overlay" onMouseDown={closePanel}>
          <div
            className="modal-content ct-form-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(860px, 100%)", maxWidth: 860 }}
          >
            <div
              className="modal-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}
            >
              <h2 className="heading-3" style={{ margin: 0 }}>
                ✍️ Manual Entry
              </h2>
              <button type="button" className="btn-secondary" onClick={closePanel}>
                ✕
              </button>
            </div>
            <form onSubmit={submitManual}>
              <div className="form-group">
                <label className="body-medium">Type of Workout</label>
                <input
                  type="text"
                  value={manName}
                  onChange={(e) => setManName(e.target.value)}
                  required
                  className="form-input"
                  placeholder="e.g., Rock Climbing, Yoga, Treadmill"
                  data-testid="manual-exercise-name"
                />
              </div>

              <div className="tracker-habit-grid" style={{ gap: 12 }}>
                <div className="form-group">
                  <label className="body-medium">Duration (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    value={manDuration}
                    onChange={(e) => setManDuration(e.target.value)}
                    required
                    className="form-input"
                    data-testid="manual-exercise-duration"
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">Weight (lbs)</label>
                  <input
                    type="number"
                    min="1"
                    value={manWeight}
                    onChange={(e) => setManWeight(e.target.value)}
                    className="form-input"
                    placeholder="e.g., 180"
                    data-testid="manual-exercise-weight"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="body-medium">Calories Burned</label>
                <input
                  type="number"
                  value={manCalories}
                  onChange={(e) => setManCalories(e.target.value)}
                  required
                  className="form-input"
                  data-testid="manual-exercise-calories"
                />
                <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                  Use AI Estimate Calories to fill this based on workout type, duration, and weight.
                </div>
              </div>

              <div className="modal-footer" style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={estimateManualCalories}
                  disabled={estimatingCalories}
                  data-testid="estimate-manual-exercise-calories"
                >
                  {estimatingCalories ? "Estimating…" : "AI Estimate Calories"}
                </button>

                <button type="submit" className="btn-primary" data-testid="log-manual-exercise">
                  Log Exercise
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const dur = Number(manDuration) || 0;
                    const cal = Number(manCalories) || 0;
                    const rate = dur > 0 ? Math.round((cal / dur) * 100) / 100 : null;

                    if (!String(manName || "").trim()) {
                      showToast?.("Enter an exercise name before saving to favorites.", "error");
                      return;
                    }

                    addExerciseFavorite({
                      exercise_name: String(manName || "").trim(),
                      calories_per_minute: rate,
                      default_duration_minutes: dur || 30,
                    });
                  }}
                  className="icon-btn"
                  title="Save this manual exercise as a favorite"
                  disabled={favBusy || !String(manName || "").trim()}
                  data-testid="favorite-manual-exercise"
>
              🤍
</button>
              </div>
            </form>
          </div>
        </div>
      )}


    

      <div className="logs-section">
        <h3 className="heading-3">
          {selectedDate === todayISO() ? "Today's Exercises" : `Exercises on ${selectedDate}`}
        </h3>

        {isAnyLoading ? (
          <p className="body-small" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
            Loading…
          </p>
        ) : combinedLogs.length === 0 ? (
          <p className="body-small" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
            No exercises logged for this day
          </p>
        ) : (
          <div className="exercise-list">
            {combinedLogs.map((log) => (
              <div key={log.id} className="exercise-item">
                <div className="exercise-details">
                  <div className="exercise-name">{log.exercise_name}</div>
                  <div className="exercise-info">
                    {log.duration_minutes} min • {log.calories_burned} cal burned
                    <span style={{ marginLeft: "8px", fontSize: "12px", color: "var(--text-muted)" }}>
                      ({log.source_label})
                    </span>
                  </div>
                </div>

                {log.kind === "app" && log.log_id ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      onClick={() => {
                        const dur = Number(log.duration_minutes) || 0;
                        const cal = Number(log.calories_burned) || 0;
                        const rate = dur > 0 ? Math.round((cal / dur) * 100) / 100 : null;

                        addExerciseFavorite({
                          exercise_name: log.exercise_name,
                          calories_per_minute: rate,
                          default_duration_minutes: dur || 30,
                        });
                      }}
                      className="icon-btn"
                      title="Add to favorites"
                      disabled={favBusy}
                      data-testid={`favorite-exercise-${log.log_id}`}
                    >
                      {log.is_favorite ? "❤️" : "🤍"}
                    </button>

                    <button
                      onClick={() => onDeleteLog(log.log_id)}
                      className="icon-btn"
                      data-testid={`delete-exercise-${log.log_id}`}
                      title="Delete app log"
                    >
                      🗑️
                    </button>
                  </div>
                ) : (
                  <span style={{ width: 34 }} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}